import type { RouteRecord } from 'vite-react-ssg';

import { MarketingLayout } from '../components/layout/marketing-layout';
import { HomePage } from '../pages/home-page';
import { RootLayout } from '../root-layout';

/**
 * Per-route SEO metadata resolved by `SeoHead` from the deepest matched route.
 * `*Key` variants are i18n keys (namespace `marketing`) translated at render
 * time; the plain `title`/`description` are literal fallbacks for the couple
 * of pages whose copy isn't translated yet (legal/dpa, legal/sub-processors).
 */
export interface RouteMeta {
  titleKey?: string;
  descriptionKey?: string;
  title?: string;
  description?: string;
}

function meta(m: RouteMeta): { meta: RouteMeta } {
  return { meta: m };
}

/**
 * Static route table for vite-react-ssg. Mirrors the previous JSX `<Routes>`
 * tree (kept in git history as marketing-routes.tsx) but as data-router route
 * objects so each concrete path can be pre-rendered to its own HTML file.
 *
 * Providers live in RootLayout (element of the pathless root), the visual
 * chrome in MarketingLayout, and every leaf carries a `handle.meta` that
 * SeoHead turns into <title>/<meta description>/<link canonical>.
 *
 * Code-splitting is preserved via `lazy` on everything past Home — a first
 * visitor almost always lands on Home, so only its chunk ships eagerly.
 */
export const routes: RouteRecord[] = [
  {
    path: '/',
    element: <RootLayout />,
    entry: 'src/root-layout.tsx',
    children: [
      {
        element: <MarketingLayout />,
        entry: 'src/components/layout/marketing-layout.tsx',
        children: [
          {
            index: true,
            Component: HomePage,
            handle: meta({ titleKey: 'home.meta.title', descriptionKey: 'home.meta.description' }),
          },
          {
            path: 'features',
            lazy: async () => ({ Component: (await import('../pages/features-page')).FeaturesPage }),
            handle: meta({ titleKey: 'features.meta.title', descriptionKey: 'features.meta.description' }),
          },
          {
            path: 'pricing',
            lazy: async () => ({ Component: (await import('../pages/pricing-page')).PricingPage }),
            handle: meta({ titleKey: 'pricing.meta.title', descriptionKey: 'pricing.meta.description' }),
          },
          {
            path: 'developers',
            lazy: async () => ({ Component: (await import('../pages/developers-page')).DevelopersPage }),
            handle: meta({ titleKey: 'developers.meta.title', descriptionKey: 'developers.meta.description' }),
          },
          {
            path: 'download',
            lazy: async () => ({ Component: (await import('../pages/download-page')).DownloadPage }),
            handle: meta({ titleKey: 'download.meta.title', descriptionKey: 'download.meta.description' }),
          },
          {
            path: 'contact',
            lazy: async () => ({ Component: (await import('../pages/contact-page')).ContactPage }),
            handle: meta({ titleKey: 'contact.meta.title', descriptionKey: 'contact.meta.description' }),
          },
          {
            path: 'help',
            lazy: async () => ({ Component: (await import('../pages/help/help-hub-page')).HelpHubPage }),
            handle: meta({ titleKey: 'help.hub.meta.title', descriptionKey: 'help.hub.meta.description' }),
          },
          {
            path: 'help/getting-started',
            lazy: async () => ({
              Component: (await import('../pages/help/getting-started-page')).GettingStartedPage,
            }),
            handle: meta({
              titleKey: 'help.gettingStarted.meta.title',
              descriptionKey: 'help.gettingStarted.meta.description',
            }),
          },
          {
            path: 'help/projects-and-clients',
            lazy: async () => ({
              Component: (await import('../pages/help/projects-and-clients-page')).ProjectsAndClientsPage,
            }),
            handle: meta({
              titleKey: 'help.projectsAndClients.meta.title',
              descriptionKey: 'help.projectsAndClients.meta.description',
            }),
          },
          {
            path: 'help/reports-and-sharing',
            lazy: async () => ({
              Component: (await import('../pages/help/reports-and-sharing-page')).ReportsAndSharingPage,
            }),
            handle: meta({
              titleKey: 'help.reportsAndSharing.meta.title',
              descriptionKey: 'help.reportsAndSharing.meta.description',
            }),
          },
          {
            path: 'help/web',
            lazy: async () => ({ Component: (await import('../pages/help/web-guide-page')).WebGuidePage }),
            handle: meta({ titleKey: 'help.web.meta.title', descriptionKey: 'help.web.meta.description' }),
          },
          {
            path: 'help/desktop',
            lazy: async () => ({ Component: (await import('../pages/help/desktop-guide-page')).DesktopGuidePage }),
            handle: meta({ titleKey: 'help.desktop.meta.title', descriptionKey: 'help.desktop.meta.description' }),
          },
          {
            path: 'help/mobile',
            lazy: async () => ({ Component: (await import('../pages/help/mobile-guide-page')).MobileGuidePage }),
            handle: meta({ titleKey: 'help.mobile.meta.title', descriptionKey: 'help.mobile.meta.description' }),
          },
          {
            path: 'help/faq',
            lazy: async () => ({ Component: (await import('../pages/help/faq-page')).FaqPage }),
            handle: meta({ titleKey: 'help.faq.meta.title', descriptionKey: 'help.faq.meta.description' }),
          },
          {
            path: 'legal/privacy',
            lazy: async () => ({ Component: (await import('../pages/legal/privacy-policy-page')).PrivacyPolicyPage }),
            handle: meta({ titleKey: 'legal.privacy.meta.title', descriptionKey: 'legal.privacy.meta.description' }),
          },
          {
            path: 'legal/cookies',
            lazy: async () => ({ Component: (await import('../pages/legal/cookie-policy-page')).CookiePolicyPage }),
            handle: meta({
              titleKey: 'legal.cookiePolicy.meta.title',
              descriptionKey: 'legal.cookiePolicy.meta.description',
            }),
          },
          {
            path: 'legal/terms',
            lazy: async () => ({
              Component: (await import('../pages/legal/terms-of-service-page')).TermsOfServicePage,
            }),
            handle: meta({ titleKey: 'legal.terms.meta.title', descriptionKey: 'legal.terms.meta.description' }),
          },
          {
            path: 'legal/impressum',
            lazy: async () => ({ Component: (await import('../pages/legal/impressum-page')).ImpressumPage }),
            handle: meta({
              titleKey: 'legal.impressum.meta.title',
              descriptionKey: 'legal.impressum.meta.description',
            }),
          },
          {
            path: 'legal/account-deletion',
            lazy: async () => ({
              Component: (await import('../pages/legal/account-deletion-page')).AccountDeletionPage,
            }),
            handle: meta({
              titleKey: 'legal.accountDeletion.meta.title',
              descriptionKey: 'legal.accountDeletion.meta.description',
            }),
          },
          {
            path: 'legal/dpa',
            lazy: async () => ({ Component: (await import('../pages/legal/dpa-page')).DpaPage }),
            handle: meta({
              title: 'Data Processing Agreement',
              description:
                'The Article 28 GDPR data processing agreement WorkingClock offers to organization customers.',
            }),
          },
          {
            path: 'legal/sub-processors',
            lazy: async () => ({ Component: (await import('../pages/legal/sub-processors-page')).SubProcessorsPage }),
            handle: meta({
              title: 'Sub-processors',
              description:
                'The third parties WorkingClock uses to process personal data on behalf of organization customers.',
            }),
          },
          {
            path: '*',
            lazy: async () => ({ Component: (await import('../pages/not-found-page')).NotFoundPage }),
            handle: meta({ titleKey: 'notFound.meta.title', descriptionKey: 'notFound.meta.description' }),
          },
        ],
      },
    ],
  },
];
